import React from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import { NamedLink } from '../../../components';

import css from './NoSearchResultsMaybe.module.css';

// The Marketplace API search schema is fixed to categoryLevel1..3, and the search page
// reads the top level as the "pub_"-prefixed public data param.
const CATEGORY_LEVEL1_PARAM = 'pub_categoryLevel1';

// How many top-level category shortcuts to offer before the empty state gets noisy.
const MAX_CATEGORY_SUGGESTIONS = 6;

// Query params that are page mechanics rather than something the user "searched for".
const NON_FILTER_PARAMS = ['page', 'mapSearch', 'sort'];

const parseSearch = search => {
  if (typeof search !== 'string' || search.length === 0) {
    return null;
  }
  try {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch (e) {
    return null;
  }
};

/**
 * Reads the human-readable free-text term out of the current query string, if any.
 */
const getKeywordsFromSearch = search => {
  const params = parseSearch(search);
  const keywords = params?.get('keywords');
  return keywords && keywords.trim().length > 0 ? keywords.trim() : null;
};

/**
 * Counts how many actual filters are applied, so the copy can suggest removing some
 * rather than just saying "nothing found".
 */
const countActiveFilters = search => {
  const params = parseSearch(search);
  if (!params) {
    return 0;
  }
  let count = 0;
  params.forEach((value, key) => {
    if (!NON_FILTER_PARAMS.includes(key) && value !== '') {
      count += 1;
    }
  });
  return count;
};

/**
 * NoSearchResultsMaybe
 *
 * A helpful empty state: it repeats what was searched, explains how to broaden the
 * search, and offers top-level category shortcuts.
 *
 * Category options come from the hosted '/listings/listing-categories.json' asset via
 * `useConfiguration()`. If that asset is absent, the category block renders nothing
 * rather than an empty grid.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.listingsAreLoaded - Whether the search response has arrived
 * @param {number} props.totalItems - Number of results
 * @param {Object} props.location - Router location (uses location.search)
 * @param {Function} props.resetAll - Clears every active filter
 * @param {boolean} [props.showCreateListingsLink] - Whether the user may post a listing
 * @returns {JSX.Element|null}
 */
const NoSearchResultsMaybe = props => {
  const config = useConfiguration();
  const { listingsAreLoaded, totalItems, location, resetAll, showCreateListingsLink } = props;

  const hasNoResult = listingsAreLoaded && totalItems === 0;
  const search = location?.search;
  const hasSearchParams = search?.length > 0;

  if (!hasNoResult) {
    return null;
  }

  const keywords = getKeywordsFromSearch(search);
  const activeFilterCount = countActiveFilters(search);

  // Categories are hosted-asset driven and may be missing or empty.
  const categories = config?.categoryConfiguration?.categories || [];
  const categorySuggestions = categories.slice(0, MAX_CATEGORY_SUGGESTIONS);

  const createListingLinkMaybe = showCreateListingsLink !== false ? (
    <NamedLink className={css.createListingLink} name="NewListingPage">
      <FormattedMessage id="SearchPage.createListing" />
    </NamedLink>
  ) : null;

  return (
    <div className={css.noSearchResults}>
      <p className={css.eyebrow}>
        <FormattedMessage id="NoSearchResultsMaybe.eyebrow" />
      </p>

      {/* Existing id kept: tests and translations reference SearchPage.noResults. */}
      <h2 className={css.heading}>
        <FormattedMessage id="SearchPage.noResults" />
      </h2>

      {keywords ? (
        <p className={css.searchedFor}>
          <FormattedMessage
            id="NoSearchResultsMaybe.searchedFor"
            values={{ keywords: <span className={css.searchedTerm}>{keywords}</span> }}
          />
        </p>
      ) : null}

      <ul className={css.suggestions}>
        {activeFilterCount > 1 ? (
          <li className={css.suggestion}>
            <FormattedMessage
              id="NoSearchResultsMaybe.tipRemoveFilters"
              values={{ count: activeFilterCount }}
            />
          </li>
        ) : null}
        {keywords ? (
          <li className={css.suggestion}>
            <FormattedMessage id="NoSearchResultsMaybe.tipBroaderTerm" />
          </li>
        ) : null}
        <li className={css.suggestion}>
          <FormattedMessage id="NoSearchResultsMaybe.tipWidenArea" />
        </li>
        <li className={css.suggestion}>
          <FormattedMessage id="NoSearchResultsMaybe.tipBeFirst" />
        </li>
      </ul>

      {categorySuggestions.length > 0 ? (
        <div className={css.categoryBlock}>
          <p className={css.categoryHeading}>
            <FormattedMessage id="NoSearchResultsMaybe.browseCategories" />
          </p>
          <div className={css.categoryLinks}>
            {categorySuggestions.map(category => (
              <NamedLink
                key={category.id}
                className={css.categoryLink}
                name="SearchPage"
                to={{ search: `?${CATEGORY_LEVEL1_PARAM}=${encodeURIComponent(category.id)}` }}
              >
                {category.name || category.id}
              </NamedLink>
            ))}
          </div>
        </div>
      ) : null}

      <div className={css.actions}>
        {hasSearchParams ? (
          <button className={css.resetAllFiltersButton} onClick={e => resetAll(e)}>
            <FormattedMessage id={'SearchPage.resetAllFilters'} />
          </button>
        ) : null}
        {createListingLinkMaybe}
      </div>
    </div>
  );
};

export default NoSearchResultsMaybe;
